<template>
  <page title="Order Generator">
    <template #controls>
      <form-button @click="reset" icon="fal fa-sync-alt">Reset</form-button>
      <form-button @click="generate" icon="fal fa-print">Generate</form-button>
    </template>

    <template #body>
      <div class="tabs">
        <button :class="{ selected: tab === 'platform1' }" @click="selectTab('platform1')">Platform1</button>
        <button :class="{ selected: tab === 'platform2' }" @click="selectTab('platform2')">Platform2</button>
      </div>

      <div class="form" v-if="tab === 'platform1'">
        <div class="input-group size-sm">
          <text-input type="number" label="Order Number" v-model="order.number" :invalid="orderNumberError" ref="number"/>
        </div>
        <div class="input-group size-md">
          <item-picker label="Client" :data="accountPickerOptions.list" :loading="accountPickerOptions.loading" @search="searchAccount" @select="accountSelected" placeholder="Account Name or ID" ref="account"/>
        </div>
        <div class="input-group size-sm">
          <text-input label="System" v-model="order.account.systemName" disabled/>
        </div>

        <div :key="'billing-dd-' + billingDropdownsKey">
          <div class="input-group size-md">
            <dropdown
              ref="termDropdown"
              label="Term"
              :list="termTypesOptions"
              v-model="order.termType"
              @select="onTermTypeSelected"
              :options="{ searchMode: 'none' }"
            />
          </div>
          <div class="input-group size-md" v-if="isPrepaidTerm">
            <dropdown
              ref="periodDropdown"
              label="Period"
              :list="periodsOptions"
              v-model="order.period"
              @select="onPeriodSelected"
              :options="{ searchMode: 'none' }"
            />
          </div>
          <div class="input-group size-sm" v-if="isCustomPeriod">
            <text-input ref="periodTimeInput" label="Period Time" v-model="order.periodTime" @keyup.enter="focusPartNumberDropdown"/>
          </div>
          <div class="input-group size-md">
            <dropdown
              ref="partNumberDropdown"
              label="Part Number"
              :list="partNumberList"
              v-model="order.partNumber"
              @select="onPartNumberSelected"
              :options="{ searchMode: 'none' }"
            />
          </div>
          <div class="input-group size-sm" v-if="isRateVisible">
            <text-input type="number" label="Rate" v-model="order.rate" :invalid="rateError" ref="rate" @keyup.enter="focusBatchSettingInput"/>
          </div>
        </div>

        <h2>ITEMS</h2>
        <div class="input-group">
          <div class="table">
            <div class="header">
              <div class="vehicle-type">Vehicle Type</div>
              <div class="location">Location</div>
              <div class="quantity">Quantity</div>
              <div class="controls"></div>
            </div>
            <div class="row" v-for="item of order.items" :key="item.id">
              <div class="vehicle-type">{{item.batchSettings.name}}</div>
              <div class="location">{{item.locations.map(i => i.name).join(', ')}}</div>
              <div class="quantity">
                {{item.quantity}}
              </div>
              <div class="controls">
                <button tabindex="-1" @click="removeOrderItem(item.id)">
                  <i class="fal fa-trash"></i>
                </button>
              </div>
            </div>
            <div class="row input">
              <div class="vehicle-type">
                <item-picker :data="batchSettingsPickerOptions.list" @search="searchBatchSetting" @select="batchSettingSelected" placeholder="Vehicle type name or ID" ref="batchSetting"/>
              </div>
              <div class="quantity">
                <text-input type="number" placeholder="Quantity" v-model.number="orderItem.quantity" @keyup.enter="addItem" @keyup.esc="resetItemAdding" ref="quantity"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form" v-if="tab === 'platform2'">
        <div class="input-group size-sm">
          <text-input type="number" label="Order Number" v-model="order.number" :invalid="orderNumberError" ref="number"/>
        </div>

        <div class="input-group size-md">
          <dropdown
            label="Organization"
            :list="organizations"
            v-model="selectedOrganization"
            @select="organizationSelected"
            :options="{ searchMode: 'filter' }"
            ref="organizationDropdown"
          />
        </div>

        <div class="input-group size-md">
          <dropdown
            label="Account"
            :list="accounts"
            v-model="selectedAccount"
            @select="platform2AccountSelected"
            :options="{ searchMode: 'filter' }"
            :disabled="!selectedOrganization || !selectedOrganization.value"
            ref="accountDropdown"
          />
        </div>

        <div :key="'billing-dd-p2-' + billingDropdownsKey">
          <div class="input-group size-md">
            <dropdown
              ref="termDropdown"
              label="Term"
              :list="termTypesOptions"
              v-model="order.termType"
              @select="onTermTypeSelected"
              :options="{ searchMode: 'none' }"
            />
          </div>
          <div class="input-group size-md" v-if="isPrepaidTerm">
            <dropdown
              ref="periodDropdown"
              label="Period"
              :list="periodsOptions"
              v-model="order.period"
              @select="onPeriodSelected"
              :options="{ searchMode: 'none' }"
            />
          </div>
          <div class="input-group size-sm" v-if="isCustomPeriod">
            <text-input ref="periodTimeInput" label="Period Time" v-model="order.periodTime" @keyup.enter="focusPartNumberDropdown"/>
          </div>

          <div class="input-group size-md">
            <dropdown
              ref="partNumberDropdown"
              label="Part Number"
              :list="partNumberList"
              v-model="order.partNumber"
              @select="onPartNumberSelected"
              :options="{ searchMode: 'none' }"
            />
          </div>

          <div class="input-group size-sm" v-if="isRateVisible">
            <text-input type="number" label="Rate" v-model="order.rate" :invalid="rateError" ref="rate" @keyup.enter="focusAfterBilling"/>
          </div>
        </div>

        <div class="input-group size-md">
          <dropdown
            ref="gatewayDropdown"
            label="Gateway"
            :list="gatewayOptions"
            v-model="selectedGateway"
            @select="onGatewaySelected"
            :options="{ searchMode: 'none' }"
            :disabled="!selectedAccount || !selectedAccount.value"
          />
        </div>

        <h2>ITEMS</h2>
        <div class="input-group">
          <div class="table no-location">
            <div class="header">
              <div class="vehicle-type">Vehicle Type</div>
              <div class="quantity">Quantity</div>
              <div class="controls"></div>
            </div>
            <div class="row" v-for="item of order.items" :key="item.id">
              <div class="vehicle-type">{{(item.deviceType && item.deviceType.name) || item.batchSettings.name}}</div>
              <div class="quantity">
                {{item.quantity}}
              </div>
              <div class="controls">
                <button tabindex="-1" @click="removeOrderItem(item.id)">
                  <i class="fal fa-trash"></i>
                </button>
              </div>
            </div>
            <div class="row input">
              <div class="vehicle-type">
                <item-picker :data="deviceTypesPickerOptions.list" @search="searchDeviceType" @select="deviceTypeSelected" placeholder="Vehicle type name or ID" ref="batchSetting"/>
              </div>
              <div class="quantity">
                <text-input type="number" placeholder="Quantity" v-model.number="orderItem.quantity" @keyup.enter="addItem" @keyup.esc="resetItemAdding" ref="quantity"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <modal title="Location" ref="locationsModal">
        <div class="locations-list">
          <list-box @confirm="locationsConfirm" :options="locationsPickerOptions.list" optionLabel="name" dataKey="id" v-model="locationsPickerOptions.value" :filter="false">
            <template #option="slot">
              <div class="location-item">
                <div class="icon">
                  <i class="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <div class="name">{{slot.option.name}}</div>
                </div>
              </div>
            </template>
          </list-box>
        </div>
        <template #footer>
          <button class="btn">Cancel</button>
          <button class="btn primary" @click="locationsConfirm">Confirm</button>
        </template>
      </modal>

      <iframe id="print-helper-frame"></iframe>
    </template>
  </page>
</template>

<script>
import Page from '@/components/Page.vue'
import FormButton from '@/components/FormButton.vue'
import TextInput from '@/components/TextInput.vue'
import ItemPicker from '@/components/ItemPicker.vue'
import Modal from '@/components/Modal.vue'
import ListBox from '@/components/ListBox.vue'
import Dropdown from '@/components/Dropdown.vue'
import gql from 'graphql-tag'
import { findBillingAccountId } from '@/lib/billing-navigation'

export default {
  name: 'OrderGeneratorView',

  components: {
    Page,
    FormButton,
    TextInput,
    ItemPicker,
    Modal,
    ListBox,
    Dropdown
  },

  methods: {
    generate () {
      const frame = document.querySelector('#print-helper-frame')

      if (!this.validateOrderNumber()) {
        return
      }

      if (!this.validateRate()) {
        return
      }

      if (!this.validatePrepaidFields()) {
        return
      }

      this.order.issuedBy = this.$store.state.auth.user.employeeId

      const payload = {
        ...this.order,
        platform: this.tab
      }

      if (this.tab === 'platform2') {
        payload.organization = this.selectedOrganization
        payload.gateway = this.selectedGateway
        payload.account = {
          value: this.selectedAccount.value,
          label: this.selectedAccount.label,
          systemId: 'platform2',
          systemName: this.selectedOrganization.label || 'Platform2'
        }
      }

      sessionStorage.setItem('order-generator', JSON.stringify(payload))

      frame.src = this.$route.path + '/print'
    },

    reset () {
      this.order.number = ''
      this.order.account = {}
      this.order.items = []
      this.order.termType = { label: '', value: null }
      this.order.period = { label: '', value: null }
      this.order.periodTime = ''
      this.order.partNumber = { label: '', value: null }
      this.order.rate = ''
      this.orderNumberError = null
      this.rateError = null
      this.accountBillingRates = []
      this.billingDropdownsKey += 1
      this.selectedOrganization = { label: '', value: null }
      this.selectedAccount = { label: '', value: null }
      this.selectedGateway = { label: '', value: null }
      this.accounts = []
      this.accountBatchSettings = []
      this.accountDeviceTypes = []
      this.batchSettingsPickerOptions.list = []
      this.deviceTypesPickerOptions.list = []

      if (this.$refs.account) this.$refs.account.reset()
      if (this.$refs.batchSetting) this.$refs.batchSetting.reset()

      this.$nextTick().then(() => {
        if (this.$refs.number) this.$refs.number.focus()
      })
    },

    selectTab (name) {
      this.tab = name

      this.setFocus()
    },

    searchAccount (searchText) {
      this.accountPickerOptions.loading = true
      this.accountPickerOptions.list = []

      this.$apollo.query({
        query: gql`
          query ($searchText: String!) {
            platform {
              accounts: searchAccount (searchText: $searchText) {
                id
                name
                system {
                  index
                  id
                  name
                }
              }
            }
          }
        `,
        variables: {
          searchText
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        this.accountPickerOptions.list = response.data.platform.accounts.map(item => {
          return {
            id: item.system.id + '_' + item.id,
            value: item.id,
            systemId: item.system.id,
            systemName: item.system.name,
            label: item.name,
            altLabel: `${item.name} <span class="system-tag ${item.system.id}">${item.system.name}</span> <span class="id-tag float-right">#${item.id}</span>`
          }
        })
      }).finally(() => {
        this.accountPickerOptions.loading = false
      })
    },

    accountSelected (item) {
      this.order.account = item
      this.order.items = []
      this.clearBillingFields()

      this.$nextTick(() => {
        if (this.$refs.termDropdown) {
          this.$refs.termDropdown.focusFromParent(true)
        }
      })

      this.$apollo.query({
        query: gql`
          query ($systemId: ID!, $accountId: Int!) {
            platform {
              account: getAccount (systemId: $systemId, accountId: $accountId) {
                id
                name
                batchSettings {
                  id
                  description
                  layout {
                    id
                    sensors {
                      index
                    }
                  }
                }
                locations {
                  id
                  name
                }
              }
            }
          }
        `,
        variables: {
          systemId: item.systemId,
          accountId: item.value
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        this.accountBatchSettings = response.data.platform.account.batchSettings
        this.locationsPickerOptions.list = response.data.platform.account.locations
      })

      this.loadBillingDefaults(item.value, item.systemId)
    },

    clearBillingFields () {
      this.order.termType = { label: '', value: null }
      this.order.period = { label: '', value: null }
      this.order.periodTime = ''
      this.order.partNumber = { label: '', value: null }
      this.order.rate = ''
      this.rateError = null
      this.accountBillingRates = []
      this.billingDropdownsKey += 1
    },

    loadBillingDefaults (platformAccountId, systemId) {
      if (platformAccountId == null) return

      findBillingAccountId(this.$apollo, platformAccountId)
        .then((billingId) => {
          if (!billingId) return null

          return this.$apollo.query({
            query: gql`
              query ($id: ID!) {
                billing {
                  account: getAccount(id: $id) {
                    account {
                      id
                      system {
                        id
                      }
                    }
                    rates {
                      normal {
                        rate
                        description
                        cycle {
                          cycle
                          period
                          partNumber
                        }
                      }
                    }
                  }
                }
              }
            `,
            variables: { id: billingId },
            fetchPolicy: 'no-cache'
          })
        })
        .then((response) => {
          if (!response) return

          const billingAccount = response.data?.billing?.account
          if (!billingAccount) return

          if (systemId && billingAccount.account?.system?.id &&
            String(billingAccount.account.system.id) !== String(systemId)) {
            return
          }

          const normals = billingAccount.rates?.normal || []
          this.accountBillingRates = normals

          const rate = this.resolveDefaultBillingRate(normals)
          if (rate) {
            this.applySingleBillingRate(rate)
            this.billingDropdownsKey += 1
            return
          }

          if (this.applySharedTermFromBilling(normals)) {
            this.billingDropdownsKey += 1
          }
        })
        .catch((error) => {
          console.error('Order Generator billing defaults:', error)
        })
    },

    resolveDefaultBillingRate (normals) {
      if (!Array.isArray(normals) || normals.length === 0) return null
      if (normals.length === 1) return normals[0]

      const signature = (rate) => [
        String(rate?.cycle?.cycle || '').toLowerCase(),
        rate?.cycle?.period == null || rate?.cycle?.period === '' ? '' : String(rate.cycle.period),
        String(rate?.cycle?.partNumber || ''),
        rate?.rate == null || rate?.rate === '' ? '' : String(Number(rate.rate))
      ].join('|')

      const first = signature(normals[0])
      if (!first || first === '|||') return null
      const allEquivalent = normals.every(rate => signature(rate) === first)
      return allEquivalent ? normals[0] : null
    },

    resolveSharedCycle (normals) {
      if (!Array.isArray(normals) || normals.length === 0) return null
      const cycles = normals
        .map(rate => String(rate?.cycle?.cycle || '').toLowerCase())
        .filter(Boolean)
      if (!cycles.length) return null
      const first = cycles[0]
      return cycles.every(cycle => cycle === first) ? first : null
    },

    applySharedTermFromBilling (normals) {
      const cycleValue = this.resolveSharedCycle(normals)
      if (!cycleValue) return false
      const term = this.termTypesOptions.find(item => item.value === cycleValue)
      if (!term) return false
      this.order.termType = { label: term.label, value: term.value }
      return true
    },

    applySingleBillingRate (rate) {
      const cycleValue = String(rate?.cycle?.cycle || '').toLowerCase()
      const term = this.termTypesOptions.find(item => item.value === cycleValue)
      if (!term) return

      this.order.termType = { label: term.label, value: term.value }

      if (cycleValue === 'prepaid' && rate.cycle?.period != null && rate.cycle.period !== '') {
        const periodVal = String(rate.cycle.period)
        const period = this.periodsOptions.find(item => item.value === periodVal)
        if (period) {
          this.order.period = { label: period.label, value: period.value }
          this.applyPartNumberFromPeriod(period.value)
        } else if (periodVal && periodVal !== 'custom') {
          this.order.period = { label: `${periodVal} Months`, value: periodVal }
          this.applyPartNumberFromPeriod(periodVal)
        }
      }

      const partFromBilling = rate.cycle?.partNumber
      if (partFromBilling) {
        const code = String(partFromBilling)
        const option = this.partNumbersOptions.find(item => item.value === code) ||
          this.partNumberList.find(item => item.value === code)
        this.order.partNumber = option
          ? { label: option.label, value: option.value }
          : { label: code, value: code }
      }

      if (rate.rate != null && rate.rate !== '') {
        this.order.rate = rate.rate
      }
    },

    applyPartNumberFromPeriod (periodValue) {
      const code = this.periodToPartNumber[String(periodValue)]
      if (!code) return
      const option = this.partNumbersOptions.find(item => item.value === code) ||
        this.partNumberList.find(item => item.value === code)
      if (!option) return
      this.order.partNumber = { label: option.label, value: option.value }
    },

    applyRateFromAccountPeriod (periodValue) {
      if (periodValue == null || periodValue === '' || periodValue === 'custom') {
        this.order.rate = ''
        this.rateError = null
        return
      }
      if (!Array.isArray(this.accountBillingRates) || !this.accountBillingRates.length) return

      const matches = this.accountBillingRates.filter(rate =>
        String(rate?.cycle?.period ?? '') === String(periodValue)
      )
      if (!matches.length) {
        this.order.rate = ''
        this.rateError = null
        return
      }

      const values = matches
        .map(rate => rate.rate)
        .filter(value => value != null && value !== '')
      if (!values.length) {
        this.order.rate = ''
        this.rateError = null
        return
      }

      const first = Number(values[0])
      if (!Number.isFinite(first) || !values.every(value => Number(value) === first)) {
        this.order.rate = ''
        this.rateError = null
        return
      }

      this.order.rate = values[0]
      this.rateError = null
    },

    locationsConfirm () {
      this.$refs.locationsModal.close(this.locationsPickerOptions.value)

      this.locationsPickerOptions.value = null
    },

    focusBatchSettingInput () {
      const picker = this.$refs.batchSetting
      if (!picker || !picker.$el) return
      const input = picker.$el.querySelector('input')
      if (input) input.focus()
    },

    focusAfterBilling () {
      if (this.tab === 'platform2' && this.$refs.gatewayDropdown) {
        this.$refs.gatewayDropdown.focusFromParent(true)
      } else {
        this.focusBatchSettingInput()
      }
    },

    onGatewaySelected (item) {
      if (!item) return
      this.$nextTick(() => {
        this.focusBatchSettingInput()
      })
    },

    focusPartNumberDropdown () {
      if (this.$refs.partNumberDropdown) {
        this.$refs.partNumberDropdown.focusFromParent(true)
      }
    },

    onTermTypeSelected (item) {
      if (!item) return
      this.$nextTick(() => {
        if (item.value === 'prepaid' && this.$refs.periodDropdown) {
          this.$refs.periodDropdown.focusFromParent(true)
        } else if (item.value !== 'prepaid') {
          this.focusPartNumberDropdown()
        }
      })
    },

    onPeriodSelected (item) {
      if (!item) return
      this.applyPartNumberFromPeriod(item.value)
      this.applyRateFromAccountPeriod(item.value)
      this.$nextTick(() => {
        if (item.value === 'custom' && this.$refs.periodTimeInput) {
          this.$refs.periodTimeInput.focus()
        } else if (this.periodToPartNumber[String(item.value)] && this.isRateVisible && this.$refs.rate) {
          this.$refs.rate.focus()
        } else if (item.value !== 'custom') {
          this.focusPartNumberDropdown()
        }
      })
    },

    onPartNumberSelected () {
      this.$nextTick(() => {
        if (this.isRateVisible && this.$refs.rate) {
          this.$refs.rate.focus()
        } else {
          this.focusAfterBilling()
        }
      })
    },

    syncPartNumberSelection () {
      const selected = this.order.partNumber && this.order.partNumber.value
      if (!selected) return
      const stillValid = this.partNumberList.some(option => option.value === selected)
      if (!stillValid) {
        this.order.partNumber = { label: '', value: null }
      }
    },

    searchBatchSetting (searchText) {
      this.batchSettingsPickerOptions.list = this.accountBatchSettings.filter(batchSetting => {
        return batchSetting.description.toLowerCase().includes(searchText.toLowerCase()) ||
          batchSetting.id === Number(searchText)
      }).sort((a, b) => {
        if (b.id === Number(searchText)) {
          return 1
        } else {
          return a.description.toLowerCase().indexOf(searchText.toLowerCase()) -
            b.description.toLowerCase().indexOf(searchText.toLowerCase())
        }
      }).map(batchSetting => {
        return {
          value: batchSetting.id,
          label: batchSetting.description,
          altLabel: `${batchSetting.description} <span class="id-tag float-right">#${batchSetting.id}</span>`,
          layout: {
            id: batchSetting.layout.id,
            name: batchSetting.layout.description,
            sensors: batchSetting.layout.sensors.length
          }
        }
      })
    },

    batchSettingSelected (item) {
      this.orderItem.vehicle.id = item.layout.id
      this.orderItem.vehicle.name = item.layout.name
      this.orderItem.vehicle.sensors = item.layout.sensors
      this.orderItem.batchSettings.id = item.value
      this.orderItem.batchSettings.name = item.label

      this.$refs.quantity.focus()
    },

    async addItem () {
      if (this.orderItem.vehicle.id === null) {
        return
      }

      if (this.orderItem.quantity === 0 || this.orderItem.quantity === '') {
        return
      }

      const locations = this.tab === 'platform2'
        ? []
        : (await this.$refs.locationsModal.open() ?? [{
          id: 0,
          name: '(None)'
        }])

      const existingOrderItem = this.order.items.find(item => {
        return item.batchSettings.id === this.orderItem.batchSettings.id &&
          item.locations.map(i => i.id).join(',') === locations.map(i => i.id).join(',')
      })

      if (existingOrderItem) {
        existingOrderItem.quantity += this.orderItem.quantity
      } else {
        this.order.items.push({
          id: Math.random(),
          batchSettings: {
            id: this.orderItem.batchSettings.id,
            name: this.orderItem.batchSettings.name
          },
          deviceType: this.orderItem.deviceType
            ? {
              id: this.orderItem.deviceType.id,
              name: this.orderItem.deviceType.name
            }
            : null,
          locations,
          vehicle: {
            id: this.orderItem.vehicle.id,
            name: this.orderItem.vehicle.name,
            sensors: this.orderItem.vehicle.sensors * this.orderItem.quantity
          },
          quantity: this.orderItem.quantity
        })
      }

      this.orderItem.vehicle.id = null
      this.orderItem.vehicle.name = ''
      this.orderItem.batchSettings.id = null
      this.orderItem.batchSettings.name = ''
      this.orderItem.deviceType = { id: null, name: '' }
      this.orderItem.quantity = ''

      if (this.$refs.batchSetting) this.$refs.batchSetting.reset()
    },

    resetItemAdding () {
      this.orderItem.quantity = ''
      if (this.$refs.batchSetting) this.$refs.batchSetting.reset()
    },

    removeOrderItem (id) {
      this.order.items = this.order.items.filter(i => i.id !== id)

      this.resetItemAdding()
    },

    setFocus () {
      this.$nextTick().then(() => {
        if (!this.$isMobile()) {
          this.$el.querySelector('input:not(:disabled)').focus()
        }
      })
    },

    validateOrderNumber () {
      const raw = this.order.number
      if (raw === '' || raw == null) {
        this.orderNumberError = 'Order number is required'
        this.$refs.number && this.$refs.number.focus()

        return false
      }
      const number = Number(raw)
      if (!Number.isFinite(number) || number <= 0) {
        this.orderNumberError = 'Invalid order number'
        this.$refs.number && this.$refs.number.focus()

        return false
      }
      this.orderNumberError = null

      return true
    },

    validateRate () {
      if (!this.isRateVisible) {
        this.rateError = null

        return true
      }

      const raw = this.order.rate
      if (raw === '' || raw == null) {
        this.rateError = 'Rate is required'
        this.$refs.rate && this.$refs.rate.focus()
        return false
      }

      const rate = Number(raw)

      if (!Number.isFinite(rate) || rate <= 0) {
        this.rateError = 'Must be greater than 0'
        this.$refs.rate && this.$refs.rate.focus()
        return false
      }
      this.rateError = null

      return true
    },

    validatePrepaidFields () {
      if (!this.isPrepaidTerm) {
        return true
      }

      if (!this.order.period || !this.order.period.value) {
        window.alert('Select a period for prepaid orders.')
        return false
      }

      if (this.order.period.value === 'custom' && !String(this.order.periodTime || '').trim()) {
        window.alert('Enter period time for a custom period.')
        return false
      }

      return true
    },

    getOrganizations () {
      this.$apollo.query({
        query: gql`
          query {
            platform2 {
              organizations: listOrganizations {
                id
                name
              }
            }
          }
        `,
        fetchPolicy: 'no-cache'
      }).then(result => {
        this.organizations = result.data.platform2.organizations.map(organization => {
          return {
            value: organization.id.toString(),
            label: organization.name
          }
        })
      }).catch(error => {
        console.error(error)
      })
    },

    getAccounts (organizationId) {
      if (!organizationId) {
        this.accounts = []
        return
      }

      return this.$apollo.query({
        query: gql`
          query ($id: ID!) {
            platform2 {
              accounts: listOrganizationAccounts(id: $id) {
                id
                name
              }
            }
          }
        `,
        variables: { id: organizationId },
        fetchPolicy: 'no-cache'
      }).then(result => {
        this.accounts = result.data.platform2.accounts.map(account => {
          return {
            value: account.id.toString(),
            label: account.name
          }
        })
        console.log(this.accounts)
      }).catch(error => {
        console.error(error)
      })
    },

    organizationSelected (item) {
      this.selectedOrganization = item || { label: '', value: null }
      this.selectedAccount = { label: '', value: null }
      this.selectedGateway = { label: '', value: null }
      this.accounts = []
      this.accountDeviceTypes = []
      this.deviceTypesPickerOptions.list = []
      this.order.items = []
      this.clearBillingFields()

      const request = this.getAccounts(this.selectedOrganization.value)
      if (request) {
        request.then(() => {
          if (this.$refs.accountDropdown) {
            this.$refs.accountDropdown.focusFromParent(true)
          }
        })
      }
    },

    platform2AccountSelected (item) {
      this.selectedAccount = item || { label: '', value: null }
      this.selectedGateway = { label: '', value: null }
      this.order.items = []
      this.clearBillingFields()
      this.accountDeviceTypes = []
      this.deviceTypesPickerOptions.list = []

      this.$nextTick(() => {
        if (this.$refs.termDropdown) {
          this.$refs.termDropdown.focusFromParent(true)
        }
      })

      if (this.selectedAccount.value) {
        this.getAccountDeviceTypes(this.selectedAccount.value)
      }
    },

    getAccountDeviceTypes (accountId) {
      if (!accountId) {
        this.accountDeviceTypes = []
        this.deviceTypesPickerOptions.list = []
        return
      }

      this.$apollo.query({
        query: gql`
          query ($id: ID!) {
            platform2 {
              deviceTypes: listAccountDeviceTypes(id: $id) {
                id
                name
                layout
                layoutTemplate
              }
            }
          }
        `,
        variables: { id: accountId },
        fetchPolicy: 'no-cache'
      }).then(result => {
        this.accountDeviceTypes = result.data.platform2.deviceTypes || []
      }).catch(error => {
        console.error(error)
      })
    },

    searchDeviceType (searchText) {
      const text = String(searchText || '').toLowerCase()
      this.deviceTypesPickerOptions.list = this.accountDeviceTypes.filter(deviceType => {
        return String(deviceType.name || '').toLowerCase().includes(text) ||
          String(deviceType.id) === String(searchText)
      }).sort((a, b) => {
        if (String(b.id) === String(searchText)) {
          return 1
        }
        return String(a.name || '').toLowerCase().indexOf(text) -
          String(b.name || '').toLowerCase().indexOf(text)
      }).map(deviceType => {
        return {
          value: deviceType.id,
          label: deviceType.name,
          altLabel: `${deviceType.name} <span class="id-tag float-right">#${deviceType.id}</span>`,
          layout: {
            id: deviceType.id,
            name: deviceType.layout || deviceType.name,
            sensors: 0
          }
        }
      })
    },

    deviceTypeSelected (item) {
      this.orderItem.vehicle.id = item.value
      this.orderItem.vehicle.name = item.label
      this.orderItem.vehicle.sensors = 0
      this.orderItem.deviceType = {
        id: item.value,
        name: item.label
      }
      // keep batchSettings in sync for shared addItem / print helpers
      this.orderItem.batchSettings.id = item.value
      this.orderItem.batchSettings.name = item.label

      if (this.$refs.quantity) this.$refs.quantity.focus()
    }
  },

  computed: {
    isPrepaidTerm () {
      return !!(this.order.termType && this.order.termType.value === 'prepaid')
    },

    isRateVisible () {
      const term = this.order.termType && this.order.termType.value
      return term === 'prepaid' || term === 'monthly'
    },

    partNumberList () {
      if (this.isRateVisible) {
        return this.partNumbersOptions
      }
      const systemId = String(this.order.account.systemId || '').toLowerCase()

      if (systemId === 'goodyear') {
        return this.partNumberOptionsGoodYear
      }
      if (systemId === 'default') {
        return this.partNumberOptionsTireview
      }
      return []
    },

    isCustomPeriod () {
      return !!(this.order.period && this.order.period.value === 'custom')
    }
  },

  data () {
    return {
      tab: 'platform1',
      billingDropdownsKey: 0,
      orderNumberError: null,
      rateError: null,
      accountBillingRates: [],
      organizations: [],
      accounts: [],
      accountDeviceTypes: [],
      selectedOrganization: { label: '', value: null },
      selectedAccount: { label: '', value: null },
      selectedGateway: { label: '', value: null },
      order: {
        number: '',
        account: {},
        items: [],
        issuedBy: '',
        termType: { label: '', value: null },
        period: { label: '', value: null },
        periodTime: '',
        partNumber: { label: '', value: null },
        rate: ''
      },

      orderItem: {
        batchSettings: {
          id: null,
          name: ''
        },
        deviceType: {
          id: null,
          name: ''
        },
        vehicle: {
          id: null,
          name: '',
          sensors: 0
        },
        quantity: ''
      },

      termTypesOptions: [
        {
          value: 'prepaid',
          label: 'Prepaid'
        },
        {
          value: 'monthly',
          label: 'Monthly'
        },
        {
          value: 'customer_satisfaction',
          label: 'Customer Satisfaction'
        },
        {
          value: 'marketing_devices',
          label: 'Marketing Devices'
        },
        {
          value: 'rma',
          label: 'RMA'
        }
      ],

      gatewayOptions: [
        {
          value: 'calamp',
          label: 'CalAmp'
        },
        {
          value: 'trackrr',
          label: 'TrackRR'
        },
        {
          value: 'geotab',
          label: 'Geotab'
        }
      ],

      partNumberOptionsTireview: [
        {
          value: '70403-36',
          label: '70403-36 - Sending Unit Battery Backup'
        },
        {
          value: '70403-38',
          label: '70403-38 - Sending Unit Non-Battery Backup'
        }

      ],

      partNumberOptionsGoodYear: [
        {
          value: '70403-32',
          label: '70403-32 - Sending Unit Battery Backup'
        },
        {
          value: '70403-40',
          label: '70403-40 - Sending Unit Non-Battery Backup'
        }

      ],

      periodsOptions: [
        {
          value: '12',
          label: '12 Months'
        },
        {
          value: '60',
          label: '60 Months'
        },
        {
          value: '84',
          label: '84 Months'
        },
        {
          value: '120',
          label: '120 Months'
        },
        {
          value: 'custom',
          label: 'Custom'
        }
      ],

      periodToPartNumber: {
        12: '70403-06',
        60: '70403-18',
        84: '70403-37',
        120: '70403-19'
      },

      partNumbersOptions: [
        {
          value: '70403-01',
          label: '70403-01 - Data-Monthly'
        },
        {
          value: '70403-42',
          label: '70403-42 - API-Monthly'
        },
        {
          value: '70403-06',
          label: '70403-06 - Prepaid 1 year'
        },
        {
          value: '70403-18',
          label: '70403-18 - Prepaid 5 years'
        },
        {
          value: '70403-37',
          label: '70403-37 - Prepaid 7 years'
        },
        {
          value: '70403-19',
          label: '70403-19 - Prepaid 10 years'
        }
      ],

      accountPickerOptions: {
        list: [],
        loading: false
      },

      batchSettingsPickerOptions: {
        list: []
      },

      deviceTypesPickerOptions: {
        list: []
      },

      locationsPickerOptions: {
        list: [],
        value: null
      }
    }
  },

  watch: {
    'order.number' () {
      this.orderNumberError = null
    },
    'order.rate' () {
      this.rateError = null
    },
    'order.termType': {
      deep: true,
      handler () {
        if (!this.isPrepaidTerm) {
          this.order.period = { label: '', value: null }
          this.order.periodTime = ''
        }
        if (!this.isRateVisible) {
          this.order.rate = ''
          this.rateError = null
        }
        this.syncPartNumberSelection()
      }
    },
    'order.period': {
      deep: true,
      handler () {
        if (!this.isCustomPeriod) {
          this.order.periodTime = ''
        }
      }
    }
  },

  mounted () {
    this.setFocus()
    this.getOrganizations()
  }
}
</script>

<style lang="less" scoped>
.tabs {
  margin-bottom: 25px;

  button {
    border: 1px solid @accent-color;
    margin-right: 20px;
    padding: 5px 20px;
    border-radius: 15px;
    background: transparent;
    color: @accent-color;
    cursor: pointer;

    &:active {
      opacity: 0.8;
    }

    &.selected {
      background: @accent-color;
      color: contrast(contrast(@accent-color));
    }
  }
}

h2 {
  font-family: 'Rajdhani', sans-serif;
  margin: 25px 0 10px 0;
}

.locations-list {
  width: 500px;
}

.location-item {
  display: flex;

  > div {
    flex: 1;
  }

  .icon {
    display: flex;
    flex: 0 0 45px;
    align-items: center;
    justify-content: center;
    font-size: 14pt;
    margin-right: 10px;
    color: tint(@accent-color, 10%);
  }

  .name {
    display: inline-block;
    width: 300px;
  }
}

.table {
  .header,
  .row {
    display: flex;
    align-items: center;

    > div {
      flex: 1;
    }

    .quantity {
      flex: 0 0 180px;
      margin-left: 1px;
      text-align: center;
    }

    .controls {
      flex: 0 0 0;
      margin-left: 5px;
    }
  }

  .header {
    border-bottom: 1px dotted fade(contrast(@main-color), 10%);

    > div {
      margin-bottom: 10px;
      opacity: 0.5;
    }
  }

  .row {
    /deep/ .vehicle-type input {
      border-radius: 8px 0 0 8px;
    }

    /deep/ .quantity .input-components {
      border-radius: 0 8px 8px 0 !important;
    }

    &.no-location {
      /deep/ .vehicle-type input,
      /deep/ .quantity .input-components {
        border-radius: 8px !important;
      }

      .row.input {
        .vehicle-type,
        .quantity {
          margin-right: 4px;
        }
      }
    }

    .location {
      margin-left: 45px;
    }

    .controls {
      button {
        width: 40px;
        height: 40px;
        border: none;
        background: transparent;
        color: @danger-color;
        font-size: 15pt;
        cursor: pointer;
        opacity: 0.5;
        transition: all 200ms;

        &:hover {
          opacity: 1;
        }

        &:active {
          opacity: 0.5;
        }
      }
    }

    &.input {
      margin-top: 10px;
    }

    &:not(.input, .controls) {
      padding: 0 10px;
    }

    &:nth-child(odd):not(.input) {
      background: fade(contrast(@main-color), 1%);
    }

    &:nth-child(even):not(.input) {
      background: fade(contrast(@main-color), 3%);
    }
  }
}

iframe {
  display: none;
}
</style>
